<template>
  <div class="watch-configurator">
    <Heading
      class="watch-configurator__title"
      tag="h2"
      size="lg"
      font-weight="ultra"
      align="center"
      text-uppercase
    >
      Dein Xiaomi Watch Konfigurator
    </Heading>
    <Heading
      class="watch-configurator__copy"
      tag="h3"
      align="center"
      size="sm"
      font-weight="regular"
      spaces-bottom
    >
      Finde Deine passende Smartwatch in der perfekten Farbe.
    </Heading>
    <div
      v-if="currentVariantEntry"
      class="watch-configurator__container-top"
    >
      <ImageGalleryContainer
        class="watch-configurator__image"
        :media-data="relevantImages"
      />
      <div class="watch-configurator__watch">
        <p><strong>Modellauswahl:</strong></p>
        <RadioGroup
          v-model="selectedWatchNumber"
          class="watch-configurator__watch-radio"
          :options="options"
          name="watch-select"
          @input="resetVariant"
        />
        <SelectInput
          v-model="selectedWatchNumber"
          id-prefix="select-watch"
          class="watch-configurator__watch-select"
          hide-prompt
          :options="options"
        />
      </div>

      <div class="watch-configurator__color">
        <strong>Farbauswahl:</strong>
        <ScVariants
          class="watch-configurator__color-select"
          :variants="colorVariants"
          @update:currentIndex="selectColor"
        />
      </div>
    </div>
    <div
      v-if="currentVariantEntry"
      class="watch-configurator__container-bottom"
    >
      <Heading
        class="watch-configurator__name"
        tag="h3"
        size="md"
      >
        {{ currentWatchEntry?.name }}
      </Heading>
      <span class="watch-configurator__variant">{{
        currentVariantEntry?.offer?.product?.name?.replace(
          currentVariantEntry?.offer?.product?.family?.name,
          ''
        )
      }}</span>

      <PricesContainer
        class="watch-configurator__price"
        :prices-data="currentVariantEntry.offer.prices"
        variant="medium"
      />
      <AddToCartButtonContainer
        class="watch-configurator__buy"
        :offer="currentVariantEntry.offer"
      />
      <LinkButton
        class="watch-configurator__button"
        tag="nuxt-link"
        :to="currentVariantEntry.path"
        transparent
      >
        Mehr erfahren
      </LinkButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useLazyAsyncData } from '#imports';

import {
  getOffersByGroup,
  loadOffersForPromotions,
  type Promotion,
  type PromotionOffer,
} from '@/lib/offers-by-group';
import type { ScopedOffer } from '@/lib/goliath/offers';
import { Heading, LinkButton, RadioGroup, SelectInput } from '@i22-td-smarthome/component-library';
import ScVariants from '@/components/smartwatch-config/sc-variants.vue';
import PricesContainer from '@/components/product-detail/prices-container.vue';
import AddToCartButtonContainer from '@/components/product-detail/add-to-cart-button-container.vue';
import ImageGalleryContainer from '@/components/product-detail/image-gallery-container.vue';
import { getImagesFromOffer } from '@/lib/goliath-media';
import {
  getFirstOfferAlternatives,
  type LocalImage,
  type TransformedAlternative,
  type TransformedProductAlternative,
} from '@i22/rocket';

type WatchData = Promotion<{
  name?: string;
  image?: string;
}>;

type WatchEntry = PromotionOffer<WatchData>;

const PRODUCT_TEASER_DATA: WatchData[] = [
  {
    articleNumber: '6941812764404',
    name: 'Xiaomi Watch 2',
    tileGroups: ['watch'],
  },
  {
    articleNumber: '6941812724750',
    name: 'Xiaomi Watch 2 Pro',
    tileGroups: ['watch'],
  },
  {
    articleNumber: '6941812757086',
    name: 'Xiaomi Watch 3',
    tileGroups: ['watch'],
  },
  {
    articleNumber: 'NOT_SET',
    name: 'Xiaomi Band 8',
    tileGroups: ['watch'],
  },
  {
    articleNumber: '6941812763285',
    name: 'Xiaomi Band 8 Pro',
    tileGroups: ['watch'],
  },
  {
    articleNumber: 'NOT_SET',
    name: 'Xiaomi Band 8 Active',
    tileGroups: ['watch'],
  },
  {
    articleNumber: '6941812756201',
    name: 'Redmi Watch 4',
    tileGroups: ['watch'],
  },
  {
    articleNumber: '6941812705827',
    name: 'Redmi Watch 3 Active',
    tileGroups: ['watch'],
  },
];

const selectedWatchNumber = ref<string | undefined>(undefined);
const selectedVariantNumber = ref<string | undefined>(undefined);

const { data: offers, error: errorData } = useLazyAsyncData(
  'xiaomi-watch-offers',
  async (): Promise<WatchEntry[]> => {
    const goliathEntries = await loadOffersForPromotions<WatchData>(PRODUCT_TEASER_DATA);
    const goliathOffers = getOffersByGroup<WatchData>(goliathEntries, 'watch');
    selectedWatchNumber.value = goliathOffers[0]?.articleNumber;
    return goliathOffers;
  }
);

// handle errors
if (errorData.value) {
  console.error(errorData.value);
}

const currentWatchEntry = computed<
  (WatchEntry & { alternatives: TransformedAlternative<ScopedOffer> }) | undefined
>(() => {
  const entry: WatchEntry | undefined = offers.value?.find(
    ({ articleNumber }) => articleNumber === selectedWatchNumber.value
  );
  if (!entry?.offer?.productAlternatives) return undefined;

  // @ts-ignore
  const alternatives = getFirstOfferAlternatives(entry.offer.productAlternatives);
  if (!alternatives.variants?.COLOR) return undefined;
  return {
    ...entry,
    alternatives,
  };
});

const currentVariantEntry = computed<TransformedProductAlternative<ScopedOffer> | undefined>(
  () =>
    currentWatchEntry.value?.alternatives?.variants?.COLOR?.items?.find(
      (alternative) => alternative?.offer?.product?.articleNumber === selectedVariantNumber.value
    ) || currentWatchEntry.value?.alternatives?.variants?.COLOR?.items?.find(({ active }) => active)
);

const options = computed(() =>
  offers.value?.map((offer) => ({
    value: offer.articleNumber,
    label: offer.name,
  }))
);

const colorVariants = computed<TransformedProductAlternative<ScopedOffer>[] | undefined>(() =>
  currentWatchEntry.value?.alternatives?.variants?.COLOR?.items?.map((item) => ({
    ...item,
    active: selectedVariantNumber.value
      ? item.offer.product?.articleNumber === selectedVariantNumber.value
      : item.active,
  }))
);

const selectColor = (index: number) => {
  selectedVariantNumber.value =
    colorVariants.value?.[index]?.offer?.product?.articleNumber || undefined;
};

const resetVariant = () => {
  selectedVariantNumber.value = undefined;
};

const relevantImages = computed<LocalImage[]>(() => {
  if (!currentVariantEntry.value?.offer) return [];
  return getImagesFromOffer<ScopedOffer>(currentVariantEntry.value?.offer);
});
</script>

<style scoped lang="scss">
@use 'assets/base' as *;

.watch-configurator {
  padding-inline: $grid-base;

  &__container-top,
  &__container-bottom {
    display: grid;
    margin-inline: auto;
    margin-bottom: $grid-base * 2;
    max-width: 1024px;
  }

  &__container-top {
    grid-template-columns: 1fr;
    grid-template-areas: 'image' 'watch' 'color';
    grid-gap: $grid-base * 2 $grid-base;

    @include mq($mq-sm) {
      grid-template-areas:
        'image'
        'watch'
        'color';
    }

    @include mq($mq-md) {
      grid-template-columns: auto min-content;
      grid-template-areas:
        'image image'
        'watch color';
    }
  }

  &__container-bottom {
    grid-template-columns: 1fr 1fr;
    grid-template-areas: 'name name' 'variant price' 'buy buy' 'button button';
    grid-gap: $grid-base $grid-base;
    align-items: flex-end;

    @include mq($mq-sm) {
      grid-template-columns: auto min-content min-content;
      grid-template-areas: 'name name name' 'variant variant variant' 'price buy button';
    }

    @include mq($mq-md) {
      grid-template-columns: auto auto min-content min-content;
      grid-template-areas:
        'name price buy button'
        'variant price buy button';
      align-items: flex-end;
    }
  }

  &__image {
    grid-area: image;
    max-width: 500px;
    width: 100%;
    margin-inline: auto;

    :deep(img) {
      display: block;
      width: 100%;
      aspect-ratio: 1;
    }
  }
  &__watch {
    grid-area: watch;

    &-radio {
      @include mq(false, $mq-sm - 1) {
        display: none;
      }

      :deep(.radio-group__container) {
        flex-direction: row;
        flex-wrap: wrap;
      }
    }

    &-select {
      margin-top: $grid-base;
      @include mq($mq-sm) {
        display: none;
      }
    }
  }
  &__color {
    grid-area: color;

    @include mq($mq-sm) {
      display: flex;
      align-items: center;
      grid-gap: $grid-base * 2;
    }

    &-select {
      margin-bottom: 0;
      padding-bottom: 0;
    }
  }
  &__name {
    grid-area: name;
    margin-top: $grid-base;
  }
  &__variant {
    grid-area: variant;
  }
  &__price {
    grid-area: price;
    justify-self: flex-end;

    @include mq($mq-sm, $mq-md - 1) {
      justify-self: flex-start;
    }

    :deep(.prices) {
      padding-bottom: 0;
    }

    :deep(.prices__main) {
      @include mq(false, $mq-sm - 1) {
        flex-direction: column;
        grid-gap: 0;
      }
    }
  }

  &__button,
  &__buy {
    @include mq(false, $mq-md - 1) {
      justify-self: center;
      width: 230px;
    }
  }

  &__button {
    grid-area: button;
  }
  &__buy {
    grid-area: buy;
  }

  &__hero {
    &-image {
      :deep(img) {
        aspect-ratio: 1;
        width: 100%;
        max-width: 500px;
      }
    }
  }
}
</style>
